import { ReactNode, useMemo } from 'react';

import { Add } from '@mui/icons-material';
import { Box, Divider, IconButton, Typography } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import {
  selectODUIMode,
  useGetAnalysesQuery,
} from 'state/originDestinationSlices';
import { selectProject } from 'state/workflowSlice';

import { ODUIMode } from 'appConstants';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';

import { RecentODList } from '../ODList';
import { useODUIMode } from '../hooks/useODUIMode';
import { AnalysisSettings } from './AnalysisSettings';

import styles from './ODSidebar.module.css';

const TITLES: Partial<Record<ODUIMode, string>> = {
  [ODUIMode.Create]: 'Create New Analysis',
  [ODUIMode.View]: 'Analyses',
};

export function ODTaskBar() {
  const uiMode = useAppSelector(selectODUIMode);
  const project_slug = useAppSelector(selectProject);

  const { currentData: analyses } = useGetAnalysesQuery(project_slug, {
    skip: !project_slug,
  });
  const { setCreateMode, setInitialMode } = useODUIMode();

  const inViewMode = uiMode === ODUIMode.View;
  const title = useMemo(() => TITLES[uiMode] ?? 'Unknown', [uiMode]);
  const count = analyses?.length ?? 0;
  const showListInCreate = uiMode === ODUIMode.Create ? count > 0 : true;

  let content: ReactNode = null;
  switch (uiMode) {
    case ODUIMode.Create:
      content = (
        <Box className={styles.create_wrapper}>
          <AnalysisSettings />
          {showListInCreate && (
            <>
              <Divider />
              <RecentODList />
              <Divider />
            </>
          )}
        </Box>
      );
      break;
    default:
      content = <RecentODList />;
      break;
  }

  const handleClose = () => {
    setInitialMode();
  };

  return (
    <Box className={styles.wrapper}>
      <div className={styles.header}>
        <Typography variant="h3" className={styles.title}>
          {title}
        </Typography>
        <Box className={styles.close_btn_wrapper}>
          {inViewMode && (
            <StlButton
              gray
              isActive
              value="Add new"
              handleClick={setCreateMode}
              icon={<Add />}
              width={88}
            />
          )}
          <IconButton size="small" title="Close" onClick={handleClose}>
            <FontAwesomeIcon name="xmark" type="sharp-solid" size="0.75rem" />
          </IconButton>
        </Box>
      </div>
      {content}
    </Box>
  );
}
